import type { AdminOverview } from "@gjwtrack/domain";
import type { ReactNode } from "react";
import { hashSlot, initials } from "../../lib/format.ts";

/** Run a command and refresh the overview; false when the server refused (the error is already on screen). */
export type Run = (name: string, input: unknown) => Promise<boolean>;

/** YouTrack's settings layout: label on the left, control on the right, explanation underneath. */
export function Row({ label, hint, children }: { label: string; hint?: string; children: ReactNode }) {
  return (
    <div className="grid grid-cols-[13rem_minmax(0,1fr)] items-start gap-4 border-b border-line/60 py-2 last:border-0">
      <div className="pt-2 text-sm text-muted">{label}</div>
      <div className="min-w-0">
        {children}
        {hint && <div className="mt-1 text-xs text-faint">{hint}</div>}
      </div>
    </div>
  );
}

export const Th = ({ children, className = "" }: { children?: ReactNode; className?: string }) => (
  <th className={`border-b border-line px-3 py-1.5 text-left text-xs font-medium text-muted uppercase ${className}`}>{children}</th>
);
export const Td = ({ children, className = "" }: { children?: ReactNode; className?: string }) => <td className={`border-b border-line/60 px-3 py-1.5 ${className}`}>{children}</td>;

/**
 * Round avatar with initials for the administration pages. Unlike `components/Badges.tsx` it takes the
 * name directly: the admin overview also lists disabled and invited users, who are not in the schema.
 */
export function PersonAvatar({ name, size = 24 }: { name: string; size?: number }) {
  return (
    <span className={`badge round chip-${hashSlot(name)}`} style={{ width: size, height: size, fontSize: size * 0.42 }} title={name} aria-hidden>
      {initials(name)}
    </span>
  );
}

/** Square project / group tile, YouTrack's icon when nothing has been uploaded. */
export function SquareIcon({ text, size = 28 }: { text: string; size?: number }) {
  return (
    <span className={`badge chip-${hashSlot(text)}`} style={{ width: size, height: size, fontSize: size * 0.42 }} aria-hidden>
      {initials(text, 1)}
    </span>
  );
}

export const userOf = (data: AdminOverview, id: number): AdminOverview["users"][number] | undefined => data.users.find((u) => u.id === id);
export const userLabel = (data: AdminOverview, id: number): string => userOf(data, id)?.name ?? `user ${id}`;
export const groupLabel = (data: AdminOverview, id: number): string => data.groups.find((g) => g.id === id)?.name ?? `group ${id}`;
export const roleLabel = (data: AdminOverview, id: number): string => data.roles.find((r) => r.id === id)?.name ?? `role ${id}`;

/** "12 Mar 2026" — the date on the project's left column. */
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
export function dateText(ms: number): string {
  const d = new Date(ms);
  return `${d.getDate()} ${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
}
